/**
 * MobileLayout
 * Wraps dashboard content on native Android:
 *  - Adds bottom padding equal to tab bar height so content is never hidden
 *  - Renders BottomTabBar at the bottom
 *  - Passes `onNavigate` callback so tabs trigger existing route changes
 *
 * On web/desktop the component is transparent — it just renders children.
 */
import React from 'react';

import { useMobileNavigation } from '../../hooks/useMobileNavigation';
import type { DashboardPage } from '../../modules/dashboard/components/DashboardContainer';
import { isNativePlatform } from '../../utils/capacitorUtils';

import BottomTabBar from './BottomTabBar';

interface MobileLayoutProps {
  children: React.ReactNode;
  /** Current dashboard page — used to keep tab highlight in sync */
  currentPage?: DashboardPage;
  /** Called when user taps a tab (resets nav stack) */
  onNavigate?: (page: DashboardPage) => void;
  /** Called to reset the navigation stack when a tab is tapped */
  onTabChange?: () => void;
  /** When true, suppress the bottom tab bar (e.g. optimizer steps manage their own nav) */
  hideTabBar?: boolean;
}

// Mobile bottom-tab → DashboardPage mapping. The optimizer tab now points to
// the AI Agent (the legacy single-optimize flow was retired). The tab id is
// preserved so persisted state and deep links keep working.
const TAB_PAGE_MAP: Record<string, DashboardPage> = {
  home:      'dashboard',
  optimizer: 'agent',
  history:   'history',
  me:        'profile',
};

const PAGE_TAB_MAP: Partial<Record<DashboardPage, string>> = {
  dashboard:       'home',
  agent:           'optimizer',
  history:         'history',
  profile:         'me',
};

const MobileLayout: React.FC<MobileLayoutProps> = ({
  children,
  currentPage,
  onNavigate,
  onTabChange,
  hideTabBar = false,
}) => {
  const isNative = isNativePlatform();
  const { activeTab, navigateTo } = useMobileNavigation();

  // Derive active tab from currentPage prop (so page-level navigation also updates tabs)
  const resolvedTab = currentPage
    ? (PAGE_TAB_MAP[currentPage] as any) ?? activeTab
    : activeTab;

  if (!isNative) {
    // On web, render children unchanged — desktop sidebar handles navigation
    return <>{children}</>;
  }

  const handleTabPress = (tab: Parameters<typeof navigateTo>[0]) => {
    navigateTo(tab);
    const page = TAB_PAGE_MAP[tab];
    // Reset the navigation stack — tabs are top-level navigation
    onTabChange?.();
    if (page) onNavigate?.(page);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        background: 'var(--vscode-bg-primary)',
      }}
    >
      {/*
       * Scrollable content area — flex 1 so it fills all height above the tab bar.
       * Reserve space for tab bar + bottom safe area.
       * mobile-scroll: hide scrollbars per mockup (light.html / dark.html).
       */}
      <div
        className={hideTabBar ? undefined : 'mobile-scroll'}
        data-ggj-native-scroll={hideTabBar ? undefined : ''}
        style={{
          flex: 1,
          overflowY: hideTabBar ? 'hidden' : 'auto',
          overflowX: 'hidden',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          paddingBottom: hideTabBar ? '0' : 'calc(48px + 8px + max(env(safe-area-inset-bottom, 0px), 8px))',
          WebkitOverflowScrolling: 'touch' as any,
          height: hideTabBar ? '100%' : undefined,
        }}
      >
        {children}
      </div>

      {/* Bottom tab bar — always above home indicator (min 16px when safe-area is 0) */}
      {!hideTabBar && (
        <div
          style={{
            flexShrink: 0,
            minHeight: 'calc(48px + 8px + max(env(safe-area-inset-bottom, 0px), 8px))',
            paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)',
          }}
        >
          <BottomTabBar activeTab={resolvedTab} onTabPress={handleTabPress} />
        </div>
      )}
    </div>
  );
};

export default MobileLayout;
